import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { TaxAuthorization, TaxAuthorizationUpsert } from '../models/tax-authorization/tax-authorization';
import { Business } from '../models/business';

@Injectable({ providedIn: 'root' })
export class TaxAuthorizationProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/tax-authorization';

  /** Authorizations filtered by business and/or authorization number. Pass the provider id +
   *  entityDefinitionCode = Business for a provider's authorizations. */
  public search(
    businessReference?: string,
    active?: boolean,
    entityDefinitionCode?: number,
    authorizationNumber?: string,
  ): Observable<ApiResponse<TaxAuthorization[]>> {
    let params = new HttpParams();
    if (businessReference) params = params.set('businessReference', businessReference);
    if (active !== undefined) params = params.set('active', active);
    if (entityDefinitionCode !== undefined) params = params.set('entityDefinitionCode', entityDefinitionCode);
    if (authorizationNumber) params = params.set('authorizationNumber', authorizationNumber);
    return this.httpClient.get<ApiResponse<TaxAuthorization[]>>(this.baseUrl, { params });
  }

  public getById(id: string): Observable<ApiResponse<TaxAuthorization>> {
    return this.httpClient.get<ApiResponse<TaxAuthorization>>(`${this.baseUrl}/${id}`);
  }

  /** The Business the current site operates under — holder of every range the site issues under its own
   *  name (#4791). Null payload when the site has no internal-company record. */
  public getSiteBusiness(): Observable<ApiResponse<Business | null>> {
    return this.httpClient.get<ApiResponse<Business | null>>(`${this.baseUrl}/site-business`);
  }

  public create(payload: TaxAuthorizationUpsert): Observable<ApiResponse<TaxAuthorization>> {
    return this.httpClient.post<ApiResponse<TaxAuthorization>>(this.baseUrl, payload);
  }

  public update(id: string, payload: TaxAuthorizationUpsert): Observable<ApiResponse<TaxAuthorization>> {
    return this.httpClient.put<ApiResponse<TaxAuthorization>>(`${this.baseUrl}/${id}`, payload);
  }

  public deactivate(id: string): Observable<ApiResponse<boolean>> {
    return this.httpClient.patch<ApiResponse<boolean>>(`${this.baseUrl}/${id}/deactivate`, {});
  }
}
